import React, { useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View, ScrollView } from 'react-native';
import Svg, { Circle, Line } from 'react-native-svg';
import { FontFamily } from '../../../../../constants/fonts';
import { CrewingHorizontalScrollView } from './webScrollStyles';

export interface KcphTrendSeries {
  labels: string[];
  // Secondary sub-label under each x-axis tick (e.g. "'26"), matching Scheduling Trend's ticks.
  labelSubLabels?: (string | undefined)[];
  estimated: number[];
  scheduled: number[];
  actual: number[];
  // A single number renders a flat target line; an array (one value per period) lets the
  // target vary by period once the backend supplies per-period targets.
  target: number | number[];
}

interface KCPHTrendCardProps {
  title: string;
  subtitle?: string;
  description?: string;
  data: KcphTrendSeries;
}

const MIN_CHART_HEIGHT = 190;
const X_AXIS_ROW_HEIGHT = Platform.OS === 'web' ? 70 : 52; // labels + sub-label + classic Windows scrollbar
const MIN_PLOT_WIDTH = 240;
const MIN_POINT_SPACING = 56;
const Y_AXIS_COL_WIDTH = 32 + 6; // yAxisCol width + its paddingRight
const TOOLTIP_GAP = 8;
const TOOLTIP_ESTIMATED_HEIGHT = 30;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const KCPHTrendCard: React.FC<KCPHTrendCardProps> = ({ title, subtitle, description, data }) => {
  // Check for no data condition
  const hasData = data.labels.length > 0 && 
    (data.estimated.length > 0 || data.scheduled.length > 0 || data.actual.length > 0);
  
  if (!hasData) {
    return (
      <View style={styles.card}>
        <View style={[styles.headerRow, Platform.OS === 'web' && styles.headerRowWeb]}>
          <View style={[styles.titleRow, Platform.OS === 'web' && styles.titleRowWeb]}>
            <Text style={styles.title}>{title}</Text>
            {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
          </View>
          {description ? <Text style={[styles.description, Platform.OS === 'web' && styles.descriptionWeb, Platform.OS === 'ios' && styles.descriptionIos]}>{description}</Text> : null}
        </View>
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>No data available</Text>
        </View>
      </View>
    );
  }

  const pointCount = Math.max(1, data.labels.length);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [selectedPoint, setSelectedPoint] = useState<{ lineKey: string; index: number } | null>(null);
  // Fill whatever vertical space the card actually has instead of a fixed 190px, minus room for the x-axis label row.
  const chartHeight = Math.max(MIN_CHART_HEIGHT, viewportHeight - X_AXIS_ROW_HEIGHT);
  const targetValues = (Array.isArray(data.target) ? data.target : [data.target])
    .map((v) => (Number.isFinite(v) ? v : 90));
  const isTargetFlat = targetValues.length <= 1 || targetValues.every((v) => v === targetValues[0]);
  // Kept for the flat-line case and as the fallback seed when normalizing the per-period series.
  const target = targetValues[targetValues.length - 1];

  // Calculate dynamic Y-axis range based on data
  const allValues = [
    ...data.estimated,
    ...data.scheduled,
    ...data.actual,
    ...targetValues,
  ].filter((v) => typeof v === 'number' && !isNaN(v));
  
  const dataMin = Math.min(...allValues, target);
  const dataMax = Math.max(...allValues, target);
  const dataRange = dataMax - dataMin || 10; // Avoid division by zero
  const padding = dataRange * 0.15; // 15% padding on each side
  
  const MIN_Y = Math.floor((dataMin - padding) / 5) * 5; // Round down to nearest 5
  const MAX_Y = Math.ceil((dataMax + padding) / 5) * 5; // Round up to nearest 5
  
  // Plot always fills the measured container width (rather than a fixed per-point pixel
  // spacing) so a single point or a few points still span the full card instead of leaving
  // the rest of the card blank.
  const chartWidth = Math.max(
    MIN_PLOT_WIDTH,
    viewportWidth - Y_AXIS_COL_WIDTH,
    pointCount * MIN_POINT_SPACING,
  );
  const needsHorizontalScroll = viewportWidth > 0 && chartWidth > viewportWidth - Y_AXIS_COL_WIDTH;
  const xAt = (index: number) => (index + 0.5) * (chartWidth / pointCount);
  const yAt = (value: number) => ((MAX_Y - clamp(value, MIN_Y, MAX_Y)) / (MAX_Y - MIN_Y)) * chartHeight;
  const getTooltipLeft = (pointX: number, tooltipWidth: number) => {
    const hitLeft = pointX - 14;
    return Math.max(4 - hitLeft, Math.min(14 - tooltipWidth / 2, chartWidth - tooltipWidth - 4 - hitLeft));
  };

  const normalizeSeries = (values: number[]) => {
    if (values.length >= pointCount) return values.slice(0, pointCount);
    const lastValue = values.length > 0 ? values[values.length - 1] : target;
    return [...values, ...Array.from({ length: pointCount - values.length }, () => lastValue)];
  };

  // Generate grid lines dynamically based on range
  const gridLines = useMemo(() => {
    const step = Math.ceil((MAX_Y - MIN_Y) / 5); // Aim for 5-6 grid lines
    const ticks = [];
    for (let i = Math.ceil(MIN_Y / step) * step; i <= MAX_Y; i += step) {
      ticks.push(i);
    }
    return ticks;
  }, [MIN_Y, MAX_Y]);

  const lines = useMemo(
    () => [
      { key: 'estimated', label: 'Estimated KCPH', color: '#3FAED8', values: normalizeSeries(data.estimated) },
      { key: 'scheduled', label: 'Scheduled KCPH', color: '#7A5AF8', values: normalizeSeries(data.scheduled) },
      { key: 'actual', label: 'Actual KCPH', color: '#FF8A00', values: normalizeSeries(data.actual) },
    ] as const,
    [data.estimated, data.scheduled, data.actual, pointCount],
  );

  const targetSeries = useMemo(() => normalizeSeries(targetValues), [targetValues, pointCount]);

  return (
    <View style={styles.card}>
      <View style={[styles.headerRow, Platform.OS === 'web' && styles.headerRowWeb]}>
        <View style={[styles.titleRow, Platform.OS === 'web' && styles.titleRowWeb]}>
          <Text style={styles.title}>{title}</Text>
          {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
        </View>
        {description ? <Text style={[styles.description, Platform.OS === 'web' && styles.descriptionWeb, Platform.OS === 'ios' && styles.descriptionIos]}>{description}</Text> : null}
      </View>

      <View style={styles.legendRow}>
        {lines.map((line) => (
          <View key={line.key} style={styles.legendItem}>
            <View style={[styles.legendStroke, { backgroundColor: line.color }]} />
            <Text style={styles.legendText}>{line.label}</Text>
          </View>
        ))}
        <View style={styles.legendItem}>
          <View style={[styles.legendStroke, styles.legendTargetStroke]} />
          <Text style={styles.legendText}>
            {isTargetFlat ? `Target ${target} KCPH` : `Target ${Math.min(...targetValues)}–${Math.max(...targetValues)} KCPH`}
          </Text>
        </View>
      </View>

      <View
        style={styles.chartViewport}
        onLayout={(event) => {
          const { height: nextHeight, width: nextWidth } = event.nativeEvent.layout;
          if (nextHeight > 0 && Math.abs(nextHeight - viewportHeight) > 1) setViewportHeight(nextHeight);
          if (nextWidth > 0 && Math.abs(nextWidth - viewportWidth) > 1) setViewportWidth(nextWidth);
        }}
      >
        <ScrollView nestedScrollEnabled showsVerticalScrollIndicator={false} scrollEnabled={false}>
          <View style={styles.chartWrap}>
              <View style={styles.plotRow}>
                <View style={[styles.yAxisCol, { height: chartHeight }]}>
                  {gridLines.map((tick) => (
                    <Text key={tick} style={[styles.yAxisLabel, { top: yAt(tick) - 6 }]}>{tick}</Text>
                  ))}
                </View>

                <CrewingHorizontalScrollView
                  horizontal
                  nestedScrollEnabled
                  scrollEnabled={needsHorizontalScroll}
                  showsHorizontalScrollIndicator={needsHorizontalScroll}
                  style={styles.plotScroll}
                >
                <View style={{ width: chartWidth }}>
                <View
                  style={[styles.plotArea, { height: chartHeight, width: chartWidth }]}
                >
                  <Svg width={chartWidth} height={chartHeight} style={{ backgroundColor: 'transparent' }}>
                  {gridLines.map((tick) => {
                    const y = yAt(tick);
                    return (
                      <Line key={tick} x1={0} y1={y} x2={chartWidth} y2={y} stroke="#ECEFF4" strokeWidth={1} />
                    );
                  })}

                  {isTargetFlat ? (
                    <Line
                      x1={0}
                      y1={yAt(target)}
                      x2={chartWidth}
                      y2={yAt(target)}
                      stroke="#7A7F86"
                      strokeWidth={1}
                      strokeDasharray="4 4"
                    />
                  ) : (
                    targetSeries.slice(1).map((value, index) => (
                      <Line
                        key={`target-line-${index}`}
                        x1={xAt(index)}
                        y1={yAt(targetSeries[index])}
                        x2={xAt(index + 1)}
                        y2={yAt(value)}
                        stroke="#7A7F86"
                        strokeWidth={1}
                        strokeDasharray="4 4"
                      />
                    ))
                  )}

                  {lines.map((line) =>
                    line.values.slice(1).map((value, index) => (
                      <Line
                        key={`${line.key}-line-${index}`}
                        x1={xAt(index)}
                        y1={yAt(line.values[index])}
                        x2={xAt(index + 1)}
                        y2={yAt(value)}
                        stroke={line.color}
                        strokeWidth={2}
                      />
                    )),
                  )}

                  {lines.map((line) =>
                    line.values.map((value, index) => (
                      <Circle
                        key={`${line.key}-point-${index}`}
                        cx={xAt(index)}
                        cy={yAt(value)}
                        r={3.4}
                        fill="#FFFFFF"
                        stroke={line.color}
                        strokeWidth={2}
                      />
                    )),
                  )}
                </Svg>
                {lines.map((line) => line.values.map((value, index) => {
                  const pointX = xAt(index);
                  const pointY = yAt(value ?? target);
                  const showAbove = pointY >= TOOLTIP_ESTIMATED_HEIGHT + TOOLTIP_GAP + 14;
                  const isSelected = selectedPoint?.lineKey === line.key && selectedPoint.index === index;
                  const tooltipValue = (value ?? 0).toFixed(1);
                  const tooltipWidth = Math.max(40, tooltipValue.length * 7 + 16);
                  return (
                    <Pressable
                      key={`${line.key}-${index}`}
                      onPress={() => setSelectedPoint((previous) => isSelected ? null : { lineKey: line.key, index })}
                      onHoverIn={() => setSelectedPoint({ lineKey: line.key, index })}
                      onHoverOut={() => setSelectedPoint((previous) => previous?.lineKey === line.key && previous.index === index ? null : previous)}
                      style={[styles.pointHitArea, { left: pointX - 14, top: Math.max(0, pointY - 14) }]}
                    >
                      {isSelected ? (
                        <View style={[styles.tooltip, { width: tooltipWidth, left: getTooltipLeft(pointX, tooltipWidth), ...(showAbove ? { bottom: 22 + TOOLTIP_GAP } : { top: 22 + TOOLTIP_GAP }) }]}> 
                          <Text style={styles.tooltipRow}>{tooltipValue}</Text>
                        </View>
                      ) : null}
                    </Pressable>
                  );
                }))}

                </View>
                <View
                  style={[styles.xAxisRow, { width: chartWidth }]}
                >
                {data.labels.map((label, index) => (
                  <View key={`${label}-${index}`} style={[styles.xAxisLabelCell, { width: chartWidth / pointCount }]}>
                    <Text style={styles.xAxisLabel}>{label}</Text>
                    {data.labelSubLabels?.[index] ? (
                      <Text style={styles.xAxisSubLabel}>{data.labelSubLabels[index]}</Text>
                    ) : null}
                  </View>
                ))}
              </View>
                </View>
                </CrewingHorizontalScrollView>
              </View>
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    padding: 14,
    flex: 1,
    minHeight: 288,
    flexDirection: 'column',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    flexWrap: 'wrap',
    width: '100%',
  },
  titleRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: 4,
    width: '100%',
  },
  title: {
    fontSize: 16,
    lineHeight: 20,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1D21',
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#838383',
  },
  description: {
    fontSize: 10,
    fontFamily: FontFamily.regular,
    color: '#838383',
    width: '100%',
    marginTop: 4,
  },
  headerRowWeb: {
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  titleRowWeb: {
    flex: 1,
    width: 'auto',
    flexWrap: 'nowrap',
    columnGap: 4,
  },
  descriptionWeb: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '400',
    width: 'auto',
    marginTop: 0,
    marginLeft: 12,
    flexShrink: 1,
    textAlign: 'right',
  },
  descriptionIos: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '400',
  },
  legendRow: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
    marginTop: 8,
    marginBottom: 10,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E3E9F3',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendStroke: {
    width: 12,
    height: 2,
  },
  legendTargetStroke: {
    height: 0,
    borderTopWidth: 2,
    borderTopColor: '#7A7F86',
    borderStyle: 'dashed',
  },
  legendText: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
  },
  chartViewport: {
    flex: 1,
    width: '100%',
  },
  chartWrap: {
    width: '100%',
  },
  plotRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  yAxisCol: {
    position: 'relative',
    width: 32,
    paddingRight: 6,
  },
  yAxisLabel: {
    position: 'absolute',
    left: 0,
    right: 6,
    fontSize: 10,
    lineHeight: 12,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
    textAlign: 'right',
  },
  plotArea: {
    position: 'relative',
  },
  plotScroll: {
    flex: 1,
    minWidth: 0,
  },
  pointHitArea: {
    position: 'absolute',
    top: 0,
    width: 28,
    height: 28,
    zIndex: 2,
  },
  tooltip: {
    position: 'absolute',
    left: -54,
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 4,
    backgroundColor: '#1E2127',
    zIndex: 3,
  },
  tooltipRow: {
    fontSize: 10,
    fontFamily: FontFamily.regular,
    color: '#FFFFFF',
  },
  xAxisRow: {
    flexDirection: 'row',
    marginTop: 6,
    paddingBottom: 4,
  },
  xAxisLabelCell: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  xAxisLabel: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#777777',
    textAlign: 'center',
  },
  xAxisSubLabel: {
    fontSize: 10,
    lineHeight: 14,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#9CA3AF',
    textAlign: 'center',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  emptyText: {
    fontSize: 14,
    color: '#9CA3AF',
    fontWeight: '500',
  },
});

export default KCPHTrendCard;
